/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Link } from '@coursera/cds-core';
import type { ButtonProps, LinkProps, Theme } from '@coursera/cds-core';
import { ExternalLinkIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/academic-integrity';

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps>(Link);

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const styles = {
  rootStyle: css({
    display: 'flex',
    alignItems: 'center',
  }),
  svgIcon: (theme: Theme) =>
    css({
      marginLeft: 'var(--cds-spacing-50)',
    }),
  buttonStyle: css({ padding: 0 }),
};

type Props = {
  link?: string;
  refetch: () => void;
};

type State = {
  isLinkAvailable: boolean;
};

class PlagiarismReportLink extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLinkAvailable: true,
    };
  }

  componentDidMount() {
    setTimeout(() => {
      // 58 seconds for link valid time
      // the turnitin link expires within 60 seconds. it's 58 seconds so that we could avoid the possible edge case that the user will click an expired link
      this.setState({
        isLinkAvailable: false,
      });
    }, 58 * 1000);
  }

  onGenerateReportClick = () => {
    const { refetch } = this.props;
    refetch();
  };

  render() {
    const { link } = this.props;
    const { isLinkAvailable } = this.state;

    return (
      <div css={styles.rootStyle}>
        {!isLinkAvailable && (
          <TrackedButton
            css={styles.buttonStyle}
            component={Link}
            data-test="generate-report"
            trackingName="generate_plagiarism_report"
            variant="ghost"
            onClick={this.onGenerateReportClick}
            size="medium"
          >
            {_t('Generate Report')}
          </TrackedButton>
        )}
        {isLinkAvailable && (
          <TrackedLink
            trackingName="view_similarity_report"
            data-test="view-report"
            href={link}
            variant="quiet"
            typographyVariant="bodyPrimary"
            target="_blank"
            rel="noopener noreferrer"
            role="link"
            icon={<ExternalLinkIcon size="medium" />}
            iconPosition="after"
          >
            {_t('View Similarity Report')}
          </TrackedLink>
        )}
      </div>
    );
  }
}

export default PlagiarismReportLink;
